import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card, CardBody, CardHeader, Typography, Button,
    Input, Tabs, TabsHeader, TabsBody, Tab, TabPanel,
    Chip, Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync, faSpinner, faCar, faUser,
    faCheckCircle, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import SearchableSelect from '../../common/SearchableSelect';
import WeekSelector from '../reports/filters/WeekSelector';

/**
 * PaymentReconciliation Component
 * Two-way lookup:
 * 1. Payment → Cars: Select batch to see all cars paid
 * 2. Car → Payments: Select car to see all payments received
 */
const PaymentReconciliation = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('payment-to-cars');

    // Payment to Cars
    const [, setSelectedPaymentOwner] = useState('');
    const [paymentHistory, setPaymentHistory] = useState(null);
    const [loadingPaymentOwner, setLoadingPaymentOwner] = useState(false);
    const [ownerInput, setOwnerInput] = useState('');
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [ownersLoading, setOwnersLoading] = useState(true);
    const [ownerWeek, setOwnerWeek] = useState(null);

    // Car to Payments
    const [, setSelectedCar] = useState('');
    const [carPayments, setCarPayments] = useState(null);
    const [loadingCar, setLoadingCar] = useState(false);
    const [carInput, setCarInput] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [carOptions, setCarOptions] = useState([]);
    const [carsLoading, setCarsLoading] = useState(true);
    const [carWeek, setCarWeek] = useState(null);

    // Batch Details Modal
    const [, setSelectedBatch] = useState(null);
    const [, setShowBatchDetails] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const fetchPaymentsByOwner = async (ownerCodeParam) => {
        const ownerCode = typeof ownerCodeParam !== 'undefined' ? ownerCodeParam : ownerInput;
        setLoadingPaymentOwner(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            if (ownerWeek) params.append('week_key', ownerWeek);

            // Use the general payments endpoint when ownerCode is empty (All Owners)
            if (ownerCode && ownerCode.trim()) {
                params.append('owner_code', ownerCode);
            }

            const url = `${rootUrl}/api/remittance/payments?${params}`;
            const result = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

            if (result.data.success) {
                setPaymentHistory(result.data);
            } else {
                setError('Error: ' + result.data.message);
            }
        } catch (error) {
            console.error('Error fetching payments by owner:', error);
            setError('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoadingPaymentOwner(false);
        }
    };

    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const res = await axios.get(`${rootUrl}/owners`, { headers: { Authorization: `Bearer ${token}` } });
                const opts = (res.data || []).map(o => ({ value: o.code, label: `${o.name} (${o.code})` }));
                setOwnerOptions(opts);
            } catch (err) {
                console.error('Error loading owners:', err);
            } finally {
                setOwnersLoading(false);
            }
        };

        fetchOwners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootUrl, token]);

    const fetchPaymentsByCar = async (plateParam) => {
        const plateNo = typeof plateParam !== 'undefined' ? plateParam : carInput;
        setLoadingCar(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            if (carWeek) params.append('week_key', carWeek);

            if (plateNo && plateNo.trim()) {
                params.append('plate_no', plateNo);
            }

            const url = `${rootUrl}/api/remittance/payments?${params}`;
            const result = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

            if (result.data.success) {
                setCarPayments(result.data);
            } else {
                setError('Error: ' + result.data.message);
            }
        } catch (error) {
            console.error('Error fetching payments by car:', error);
            setError('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoadingCar(false);
        }
    };

    useEffect(() => {
        const fetchCars = async () => {
            try {
                const res = await axios.get(`${rootUrl}/cars/plates`, { headers: { Authorization: `Bearer ${token}` } });
                const data = res.data || [];
                const opts = data.map(item => {
                    if (typeof item === 'string') return { value: item, label: item };
                    if (item.plate_no) return { value: item.plate_no, label: `${item.plate_no} (${item.make || ''} ${item.model || ''})` };
                    return { value: item.value || item.code || item.plate, label: item.label || item.name || item.plate_no };
                });
                setCarOptions(opts);
            } catch (err) {
                console.error('Error loading cars:', err);
            } finally {
                setCarsLoading(false);
            }
        };

        fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootUrl, token]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getPaymentMethodBadge = (method) => {
        const colors = {
            'BANK_TRANSFER': 'bg-blue-100 text-blue-800',
            'CHEQUE': 'bg-purple-100 text-purple-800',
            'MPESA': 'bg-green-100 text-green-800',
            'CASH': 'bg-yellow-100 text-yellow-800',
            'OTHER': 'bg-gray-100 text-gray-800'
        };
        return colors[method] || colors['OTHER'];
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <Typography variant="h4" className="font-bold text-[#2a2e30] mb-1">
                        Payment Reconciliation
                    </Typography>
                    <Typography variant="small" className="text-gray-600">
                        Two-way lookup: View payments by owner or car
                    </Typography>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-4">
                {error && (
                    <Alert
                        color="red"
                        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                        className="mb-4"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert
                        color="green"
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        className="mb-4"
                        onClose={() => setSuccess(null)}
                    >
                        {success}
                    </Alert>
                )}
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8 min-h-screen">
                <Card className="shadow-lg overflow-visible min-h-screen">
                    <CardBody className="p-0 overflow-visible">
                        <Tabs value={activeTab} className="overflow-visible">
                            <TabsHeader className="relative z-0 rounded-none bg-transparent p-4 border-b border-gray-200">
                                <Tab
                                    value="payment-to-cars"
                                    onClick={() => setActiveTab('payment-to-cars')}
                                    className={`px-4 py-2 font-semibold transition-all ${activeTab === 'payment-to-cars'
                                        ? 'text-[#FF5733] border-b-2 border-[#FF5733]'
                                        : 'text-gray-600 border-b-2 border-transparent'
                                        }`}
                                >
                                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                                    Payments by Owner
                                </Tab>
                                <Tab
                                    value="car-to-payments"
                                    onClick={() => setActiveTab('car-to-payments')}
                                    className={`px-4 py-2 font-semibold transition-all ${activeTab === 'car-to-payments'
                                        ? 'text-[#FF5733] border-b-2 border-[#FF5733]'
                                        : 'text-gray-600 border-b-2 border-transparent'
                                        }`}
                                >
                                    <FontAwesomeIcon icon={faCar} className="mr-2" />
                                    Payments by Car
                                </Tab>
                            </TabsHeader>

                            <TabsBody>
                                {/* Tab 1: Payments by Owner */}
                                <TabPanel value="payment-to-cars" className="p-6 min-h-screen">
                                    <div className="space-y-6">
                                        {/* Search Card */}
                                        <Card className="bg-blue-50 border border-blue-200 overflow-visible">
                                            <CardBody className="p-4">
                                                <Typography variant="small" className="font-semibold text-gray-700 mb-3">
                                                    Search Payments by Owner
                                                </Typography>
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex flex-col sm:flex-row gap-3">
                                                        <SearchableSelect
                                                            placeholder="Search owner..."
                                                            value={ownerInput}
                                                            onChange={(val) => {
                                                                setOwnerInput(val || '');
                                                                // fetch immediately using the new value
                                                                fetchPaymentsByOwner(val || '');
                                                            }}
                                                            loading={ownersLoading}
                                                            options={[{ value: '', label: 'All Owners' }, ...ownerOptions]}
                                                            className="flex-1 bg-white"
                                                        />
                                                        <Button onClick={() => fetchPaymentsByOwner()} disabled={loadingPaymentOwner} className="bg-[#FF5733] whitespace-nowrap">
                                                            {loadingPaymentOwner ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                                                    Searching...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon icon={faSync} className="mr-2" />
                                                                    Search
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                        <Input
                                                            type="date"
                                                            label="From Date (Optional)"
                                                            value={dateFrom}
                                                            onChange={(e) => setDateFrom(e.target.value)}
                                                        />
                                                        <Input
                                                            type="date"
                                                            label="To Date (Optional)"
                                                            value={dateTo}
                                                            onChange={(e) => setDateTo(e.target.value)}
                                                        />
                                                        <WeekSelector
                                                            selectedWeek={ownerWeek}
                                                            onWeekChange={(w) => setOwnerWeek(w)}
                                                            className=""
                                                        />
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>

                                        {/* Results */}
                                        {paymentHistory && (
                                            <Card className="shadow rounded-lg overflow-hidden border">
                                                <CardHeader className="bg-white p-4 border-b">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <Typography variant="h6" className="font-bold">
                                                                {paymentHistory.owner_code || 'All Owners'}
                                                            </Typography>
                                                            <Typography variant="small" className="text-gray-600">
                                                                {paymentHistory.payment_count} allocation(s)
                                                            </Typography>
                                                        </div>
                                                        <Chip value={formatCurrency(paymentHistory.total_received)} className="bg-[#FF5733] text-white font-bold" />
                                                    </div>
                                                </CardHeader>
                                                <CardBody className="p-0">
                                                    {(() => {
                                                        const paymentsList = paymentHistory.payments_by_batch?.flatMap(batch => (
                                                            batch.cars.map(c => ({
                                                                ...c,
                                                                batch_reference: batch.batch_reference,
                                                                payment_date: batch.payment_date,
                                                                payment_method: batch.payment_method,
                                                                batch_total: batch.batch_total
                                                            }))
                                                        )) || [];

                                                        return paymentsList.length > 0 ? (
                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full table-auto text-sm">
                                                                    <thead className="bg-gray-50">
                                                                        <tr className="text-left">
                                                                            <th className="px-4 py-3 font-semibold text-gray-700">Batch</th>
                                                                            <th className="px-4 py-3 font-semibold text-gray-700">Payment Date</th>
                                                                            <th className="px-4 py-3 font-semibold text-gray-700">Week</th>
                                                                            <th className="px-4 py-3 font-semibold text-gray-700">Plate</th>
                                                                            <th className="px-4 py-3 font-semibold text-gray-700">Method</th>
                                                                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100 bg-white">
                                                                        {paymentsList.map((row, idx) => (
                                                                            <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                                                                                <td className="px-4 py-3 font-semibold text-gray-900">{row.batch_reference}</td>
                                                                                <td className="px-4 py-3 text-gray-700">{formatDate(row.payment_date)}</td>
                                                                                <td className="px-4 py-3 font-semibold">{row.week_key}</td>
                                                                                <td className="px-4 py-3 text-gray-700">{row.plate_no}</td>
                                                                                <td className="px-4 py-3"><Chip value={row.payment_method} className={`font-semibold text-xs ${getPaymentMethodBadge(row.payment_method)}`} /></td>
                                                                                <td className="px-4 py-3 text-right font-bold text-[#FF5733]">{formatCurrency(row.allocated_amount)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <div className="p-8 text-center">
                                                                <Typography className="text-gray-600">No payments found</Typography>
                                                            </div>
                                                        );
                                                    })()}
                                                </CardBody>
                                            </Card>
                                        )}
                                    </div>
                                </TabPanel>

                                {/* Tab 2: Payments by Car */}
                                <TabPanel value="car-to-payments" className="p-6 min-h-screen">
                                    <div className="space-y-6">
                                        {/* Search Card */}
                                        <Card className="bg-green-50 border border-green-200 overflow-visible">
                                            <CardBody className="p-4">
                                                <Typography variant="small" className="font-semibold text-gray-700 mb-3">
                                                    Search Payments by Car
                                                </Typography>
                                                <div className="space-y-3">
                                                    <div className="flex flex-col gap-3">
                                                        <div className="flex flex-col sm:flex-row gap-3">
                                                            <SearchableSelect
                                                                placeholder="Search car plate..."
                                                                value={carInput}
                                                                onChange={(val) => {
                                                                    setCarInput(val || '');
                                                                    // fetch immediately using new value
                                                                    fetchPaymentsByCar(val || '');
                                                                }}
                                                                loading={carsLoading}
                                                                options={[{ value: '', label: 'All Cars' }, ...carOptions]}
                                                                className="flex-1 bg-white"
                                                            />
                                                            <Button
                                                                onClick={() => fetchPaymentsByCar()}
                                                                disabled={loadingCar}
                                                                className="bg-[#FF5733] whitespace-nowrap"
                                                            >
                                                                {loadingCar ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                                                        Searching...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                                                                        Search
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                            <Input
                                                                type="date"
                                                                label="From Date (Optional)"
                                                                value={dateFrom}
                                                                onChange={(e) => setDateFrom(e.target.value)}
                                                            />
                                                            <Input
                                                                type="date"
                                                                label="To Date (Optional)"
                                                                value={dateTo}
                                                                onChange={(e) => setDateTo(e.target.value)}
                                                            />
                                                            <WeekSelector
                                                                selectedWeek={carWeek}
                                                                onWeekChange={(w) => setCarWeek(w)}
                                                                className=""
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>

                                        {/* Results */}
                                        {carPayments && (
                                            <Card className="border-l-4 border-green-600">
                                                <CardHeader className="bg-gray-50 p-4 border-b border-gray-200">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <Typography variant="h6" className="font-bold">
                                                                {carPayments.plate_no || carPayments.car?.plate_no || 'All Cars'}
                                                            </Typography>
                                                            <Typography variant="small" className="text-gray-600">
                                                                {(carPayments.car ? `${carPayments.car.make || ''} ${carPayments.car.model || ''} • Owner: ${carPayments.car.owner_code || '-'}` : 'All owners and cars')}
                                                            </Typography>
                                                        </div>
                                                        <Chip
                                                            value={formatCurrency(carPayments.total_received)}
                                                            className="bg-green-600 text-white font-bold"
                                                        />
                                                    </div>
                                                </CardHeader>
                                                <CardBody className="p-0">
                                                    {(() => {
                                                        const paymentsList = carPayments.payments || carPayments.payments_by_batch?.flatMap(batch => (
                                                            batch.cars.map(c => ({
                                                                ...c,
                                                                batch_reference: batch.batch_reference,
                                                                payment_date: batch.payment_date,
                                                                payment_method: batch.payment_method,
                                                                batch_total: batch.batch_total
                                                            }))
                                                        )) || [];

                                                        return paymentsList.length > 0 ? (
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-sm">
                                                                    <thead className="bg-gray-50 border-b border-gray-200">
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Batch Reference</th>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Payment Date</th>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Week</th>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Owner</th>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Plate</th>
                                                                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Method</th>
                                                                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {paymentsList.map((payment, idx) => (
                                                                            <tr
                                                                                key={idx}
                                                                                className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}
                                                                            >
                                                                                <td className="px-4 py-3 font-semibold text-gray-900">{payment.batch_reference}</td>
                                                                                <td className="px-4 py-3 text-gray-700">{formatDate(payment.payment_date)}</td>
                                                                                <td className="px-4 py-3 font-semibold">{payment.week_key}</td>
                                                                                <td className="px-4 py-3 text-gray-700">{payment.owner_code || '-'}</td>
                                                                                <td className="px-4 py-3 text-gray-700">{payment.plate_no || '-'}</td>
                                                                                <td className="px-4 py-3">
                                                                                    <Chip value={payment.payment_method} className={`font-semibold text-xs ${getPaymentMethodBadge(payment.payment_method)}`} />
                                                                                </td>
                                                                                <td className="px-4 py-3 text-right font-bold text-[#FF5733]">{formatCurrency(payment.allocated_amount)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <div className="p-8 text-center">
                                                                <Typography className="text-gray-600">No payments found</Typography>
                                                            </div>
                                                        );
                                                    })()}
                                                </CardBody>
                                            </Card>
                                        )}
                                    </div>
                                </TabPanel>
                            </TabsBody>
                        </Tabs>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default PaymentReconciliation;
